import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, debugExportUrl, type DebugSessionView } from '../../api/client.ts';
import { EventLogStream } from './EventLogStream.tsx';
import { SdkLogTab } from './SdkLogTab.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const when = (iso: string) => `${iso.slice(0, 16).replace('T', ' ')} UTC`;

/**
 * Settings → SDK Debugger (BB-35): a capture window of up to 60 minutes for one installation id (from the sample
 * app's Debug screen) or external id; its batches appear live and export as JSON or CSV. With «Collect SDK logs» the
 * devices of internal groups also upload the SDK's own log (BB-157).
 */
export function SdkDebuggerPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/sdk-debugger' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/sdk-debugger' });
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['debug-sessions', environmentId], queryFn: () => api.debugSessions(environmentId) });
  const [form, setForm] = useState({ targetKind: 'installation' as 'installation' | 'external_id', targetValue: '', minutes: 30, note: '', collectSdkLogs: false });
  const [viewing, setViewing] = useState<DebugSessionView | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['debug-sessions', environmentId] });
  const create = useMutation({
    mutationFn: () => api.createDebugSession(environmentId, { targetKind: form.targetKind, targetValue: form.targetValue.trim(), minutes: form.minutes, ...(form.note.trim() ? { note: form.note.trim() } : {}), ...(form.collectSdkLogs ? { collectSdkLogs: true } : {}) }),
    onSuccess: (session) => {
      setForm({ ...form, targetValue: '', note: '' });
      setViewing(session);
      refresh();
    },
  });
  const end = useMutation({ mutationFn: (id: string) => api.endDebugSession(environmentId, id), onSuccess: refresh });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (form.targetValue.trim()) create.mutate();
  };

  return (
    <section>
      <h1>SDK Debugger</h1>
      <p className="hint">Capture every request of one device or user for a while. The installation ID is on the Debug screen of the sample apps.</p>
      <form className="stack-form" aria-label="Start a debug session" onSubmit={submit}>
        <label>
          Capture by
          <select value={form.targetKind} onChange={(e) => setForm({ ...form, targetKind: e.target.value as typeof form.targetKind })}>
            <option value="installation">Installation ID</option>
            <option value="external_id">External ID</option>
          </select>
        </label>
        <label>
          {form.targetKind === 'installation' ? 'Installation ID' : 'External ID'}
          <input value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: e.target.value })} />
        </label>
        <label>
          Duration
          <select value={form.minutes} onChange={(e) => setForm({ ...form, minutes: Number(e.target.value) })}>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes</option>
          </select>
        </label>
        <label>
          Note
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Pixel 8, release build" />
        </label>
        <label>
          <input type="checkbox" checked={form.collectSdkLogs} onChange={(e) => setForm({ ...form, collectSdkLogs: e.target.checked })} /> Collect SDK logs (devices of internal groups only)
        </label>
        <Button type="submit" disabled={create.isPending || !form.targetValue.trim()}>
          Start session
        </Button>
        {create.isError && <span role="alert">{errorText(create.error)}</span>}
      </form>

      {sessions.isPending ? (
        <p aria-busy="true">Loading sessions…</p>
      ) : sessions.isError ? (
        <ErrorState title="Sessions could not be loaded" message={errorText(sessions.error)} onRetry={() => void sessions.refetch()} />
      ) : sessions.data.items.length === 0 ? (
        <EmptyState title="No debug sessions" description="Start one above to capture a device's requests." />
      ) : (
        <table className="table" aria-label="Debug sessions">
          <thead>
            <tr>
              <th scope="col">Target</th>
              <th scope="col">Started</th>
              <th scope="col">Until</th>
              <th scope="col">Captured</th>
              <th scope="col">SDK log</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.data.items.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.targetKind === 'installation' ? 'Installation' : 'External ID'} <code>{s.targetValue}</code>
                  {s.note && <div className="hint">{s.note}</div>}
                </td>
                <td>
                  {when(s.createdAt)}
                  {s.createdBy?.email ? ` by ${s.createdBy.email}` : ''}
                </td>
                <td>{s.active ? when(s.expiresAt) : <span className="tag tag--neutral">Ended</span>}</td>
                <td>{s.captured}</td>
                <td>{s.collectSdkLogs ? `${s.sdkLogLines} lines` : '—'}</td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => setViewing(s)}>
                    View log
                  </Button>
                  {s.active && (
                    <Button size="sm" variant="ghost" onClick={() => end.mutate(s.id)} disabled={end.isPending}>
                      End
                    </Button>
                  )}{' '}
                  <a href={debugExportUrl(environmentId, s.id, 'json')}>JSON</a> · <a href={debugExportUrl(environmentId, s.id, 'csv')}>CSV</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {viewing && (
        <section aria-label={`Log of session ${viewing.targetValue}`}>
          <h2>Log of {viewing.targetValue}</h2>
          <EventLogStream environmentId={environmentId} filters={{ sessionId: viewing.id }} emptyText="Nothing captured yet: open the app on that device." />
          {viewing.collectSdkLogs && <SdkLogTab environmentId={environmentId} session={viewing} />}
        </section>
      )}
    </section>
  );
}
